/**
 * The customer's website as checked after parsing: does the domain answer,
 * is it a real company, does it look like a metals buyer. Shown under the
 * customer fields with a "check again" for after an email or URL edit.
 */
import { useState } from "react";
import { recheckCustomerWebsite, RfqCustomerWebsite } from "./api";

const STATUS: Record<RfqCustomerWebsite["status"], { label: string; cls: string }> = {
  verified: { label: "Website verified", cls: "bg-green-100 text-green-800 border-green-300" },
  unreachable: { label: "Website did not answer", cls: "bg-red-50 text-red-700 border-red-300" },
  parked: { label: "Domain parked / no company content", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  freemail: { label: "Personal email, no company site", cls: "bg-gray-100 text-gray-600 border-gray-300" },
  unknown: { label: "Website not checked yet", cls: "bg-gray-100 text-gray-600 border-gray-300" },
};

export function CustomerWebsite({
  rfqId,
  website,
  email,
  onChecked,
}: {
  rfqId: string;
  website: RfqCustomerWebsite | null | undefined;
  email: string;
  onChecked: (website: RfqCustomerWebsite | null) => void;
}) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const status = website?.status || "unknown";
  const meta = STATUS[status];
  const domainHint = email.includes("@") ? email.split("@")[1] : "";

  const check = async () => {
    if (!rfqId) return;
    setBusy(true);
    setError("");
    try {
      const r = await recheckCustomerWebsite(rfqId, url.trim() || undefined);
      onChecked(r.website);
      setUrl("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 font-medium ${meta.cls}`}>{meta.label}</span>
        {website?.url && status !== "freemail" && (
          <a className="text-blue-700 hover:underline" href={website.url} target="_blank" rel="noreferrer">
            {website.domain || website.url}
          </a>
        )}
        {status === "verified" && website?.relevant === true && (
          <span className="rounded-full border border-green-300 bg-green-50 px-2 py-0.5 font-medium text-green-800">
            Buys metals: likely
          </span>
        )}
        {status === "verified" && website?.relevant === false && (
          <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-medium text-amber-800">
            Buys metals: unlikely
          </span>
        )}
        {website?.checkedAt && (
          <span className="text-gray-500">checked {new Date(website.checkedAt).toLocaleDateString()}</span>
        )}
      </div>
      {website?.title && status === "verified" && <div className="mt-1 text-gray-800">{website.title}</div>}
      {website?.summary && <div className="mt-0.5 text-gray-600">{website.summary}</div>}
      {website?.relevance && status === "verified" && <div className="mt-0.5 text-gray-600">{website.relevance}</div>}
      {!website && domainHint && (
        <div className="mt-0.5 text-gray-500">Will check {domainHint} when the RFQ is saved.</div>
      )}
      {rfqId && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            className="w-64 rounded border border-gray-300 bg-white px-2 py-1 text-xs"
            placeholder="Website, if different from the email domain"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-1 font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            disabled={busy}
            onClick={check}
          >
            {busy ? "Checking..." : website ? "Check again" : "Check now"}
          </button>
          {error && <span className="text-red-700">{error}</span>}
        </div>
      )}
    </div>
  );
}
